import { AnimatePresence, motion } from 'motion/react';
import type { ComponentType } from 'react';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { ASSET_TYPE } from '@/types/asset';
import ArrowSelect from '@/components/Icons/ArrowSelect';
import LutsIcon from '@/components/Icons/Sidebar/Luts';
import MotionGraphicsIcon from '@/components/Icons/Sidebar/MotionGraphics';
import MusicIcon from '@/components/Icons/Sidebar/Music';
import SoundEffectsIcon from '@/components/Icons/Sidebar/SoundEffects';
import StockVideoIcon from '@/components/Icons/Sidebar/StockVideo';
import { cn } from '@/lib/utils';
import { Button, Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@music-vine/cadence/ui';
import type { FormValues } from '@/features/GlobalSearch/globalSearchForm';
import type { AssetType } from '@/features/GlobalSearch/types/index';

// Shared constants
const ICON_SIZE = 'size-4';
const FOCUS_DELAY_MS = 100;

// Shared class utilities
const getIconClasses = (isActive: boolean) => cn(ICON_SIZE, isActive ? 'text-pink-500 dark:text-white' : 'text-gray-800 dark:text-gray-300');
const selectItemClasses = cn('cursor-pointer rounded-2xl px-3 py-1 text-sm transition-all duration-300', 'hover:bg-gray-50 focus:bg-gray-100 dark:focus:bg-gray-900 dark:focus:text-gray-300 dark:hover:bg-gray-900 dark:hover:text-gray-300', 'data-[state=checked]:bg-pink-100 data-[state=checked]:text-pink-500 dark:data-[state=checked]:bg-gray-700 dark:data-[state=checked]:text-white');
const mobileButtonBaseClasses = 'rounded-lg! flex items-center gap-0 border px-3 py-1.5';
const mobileButtonActiveClasses = 'dark:border-white! dark:bg-[#4E5152]! hover:border-pink-500! hover:bg-pink-100! dark:hover:border-white! dark:hover:bg-[#4E5152]! overflow-hidden border-pink-500 bg-pink-100 text-pink-500 dark:text-white';
const mobileButtonInactiveClasses = 'dark:bg-gray-900! dark:border-gray-900! hover:border-gray-50! dark:hover:border-gray-900! bg-gray-50! border-gray-50 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-900';

// Asset type configuration
const ASSET_TYPE_CONFIG: {
  type: AssetType;
  label: string;
  Icon: ComponentType<{
    className?: string;
  }>;
}[] = [{
  type: 'music',
  label: 'Music',
  Icon: MusicIcon
}, {
  type: 'sfx',
  label: 'Sound effects',
  Icon: SoundEffectsIcon
}, {
  type: ASSET_TYPE.STOCK_VIDEO,
  label: 'Stock video',
  Icon: StockVideoIcon
}, {
  type: 'motion',
  label: 'Motion graphics',
  Icon: MotionGraphicsIcon
}, {
  type: 'luts',
  label: 'LUTs',
  Icon: LutsIcon
}];
interface TransparentSelectAssetProps {
  isOpen: boolean;
  onAssetTypeChange: (value: AssetType) => void;
  onFocusInput?: () => void;
  onOpenChange: (open: boolean) => void;
}
export const TransparentSelectAsset = ({
  isOpen,
  onOpenChange,
  onAssetTypeChange,
  onFocusInput
}: TransparentSelectAssetProps) => {
  const {
    watch
  } = useFormContext<FormValues>();
  const currentAssetType = watch('assetType');
  const handleValueChange = useCallback((value: AssetType) => {
    onAssetTypeChange(value);
    onOpenChange(false);
    setTimeout(() => {
      onFocusInput?.();
    }, FOCUS_DELAY_MS);
  }, [onAssetTypeChange, onOpenChange, onFocusInput]);
  return <Select onOpenChange={onOpenChange} onValueChange={handleValueChange} value={currentAssetType} data-sentry-element="Select" data-sentry-component="TransparentSelectAsset" data-sentry-source-file="AssetTypeSelect.tsx">
      <SelectTrigger className={cn('h-[22px] w-auto min-w-0 cursor-pointer rounded-3xl border-none py-0 pl-0 text-left text-pink-500 text-sm sm:-ml-2 sm:p-2 sm:text-center', 'shrink-0 focus-visible:ring-2 focus-visible:ring-gray-200 dark:text-pink-500 dark:focus-visible:ring-gray-700', isOpen ? 'bg-white dark:bg-gray-900' : 'bg-transparent dark:bg-transparent')} icon={<ArrowSelect className="size-3 min-w-3" />} data-sentry-element="SelectTrigger" data-sentry-source-file="AssetTypeSelect.tsx">
        <SelectValue placeholder="Select asset" data-sentry-element="SelectValue" data-sentry-source-file="AssetTypeSelect.tsx" />
      </SelectTrigger>
      <SelectContent align="start" className={cn('z-[1000] rounded-2xl drop-shadow-[0_1px_4px_rgba(0,0,0,0.15)]', 'top-0 -left-1.5 dark:bg-gray-950 dark:drop-shadow-[0_1px_4px_rgba(255,255,255,0.1)]')} position="popper" side="top" sideOffset={-32} data-sentry-element="SelectContent" data-sentry-source-file="AssetTypeSelect.tsx">
        <SelectGroup data-sentry-element="SelectGroup" data-sentry-source-file="AssetTypeSelect.tsx">
          {ASSET_TYPE_CONFIG.map(({
          type,
          label
        }) => <SelectItem className={selectItemClasses} hideCheck key={type} value={type}>
              {label}
            </SelectItem>)}
        </SelectGroup>
      </SelectContent>
    </Select>;
};
interface MobileAssetTypeSelectorProps {
  currentAssetType: AssetType;
  onAssetTypeChange: (value: AssetType) => void;
  onFocusInput?: () => void;
}
export const MobileAssetTypeSelector = ({
  currentAssetType,
  onAssetTypeChange,
  onFocusInput
}: MobileAssetTypeSelectorProps) => {
  const handleAssetTypeChange = useCallback((type: AssetType) => {
    onAssetTypeChange(type);
    setTimeout(() => {
      onFocusInput?.();
    }, FOCUS_DELAY_MS);
  }, [onAssetTypeChange, onFocusInput]);
  return <div className="flex gap-2" data-sentry-component="MobileAssetTypeSelector" data-sentry-source-file="AssetTypeSelect.tsx">
      {ASSET_TYPE_CONFIG.map(({
      type,
      label,
      Icon
    }) => {
      const isActive = currentAssetType === type;
      return <Button border className={cn(mobileButtonBaseClasses, isActive ? mobileButtonActiveClasses : mobileButtonInactiveClasses)} key={type} onClick={() => handleAssetTypeChange(type)} variant="subtle">
            <Icon className={getIconClasses(isActive)} />
            <AnimatePresence initial={false}>
              {isActive && <motion.span animate={{
            width: 'auto',
            opacity: 1,
            marginLeft: 8
          }} className="whitespace-nowrap text-base" exit={{
            width: 0,
            opacity: 0,
            marginLeft: 0
          }} initial={{
            width: 0,
            opacity: 0,
            marginLeft: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }}>
                  {label}
                </motion.span>}
            </AnimatePresence>
          </Button>;
    })}
    </div>;
};